import type { AddItemParams, ICartStore } from "../common-cart/CommonCartStore";
import { LazyStore } from "../../utils/Store";
import { EventSourcedCartStoreImpl } from "./EventSourcedCartStore";
import { SessionStore } from "../../uidispatcherapi/SessionStore";
import * as CustomerCartServerRequests from "../../server/CustomerCartServerRequests";
import { SavedCartActions } from "../../actions/SavedCartActions";
import { CartDeletedPayload, ItemAddedPayload, ItemQuantitySetPayload, ItemRemovedPayload } from "./CustomerCartEvents";
import { floorToThreeDecimalPlaces, minQuantity } from "../../utils/NumberUtil";
import { SavedCartContextStore } from "../common-cart/CartContextStore";
import { CustomerDataStore } from "../CustomerDataStore";
import { CustomerCartEventRepository } from "../common-cart/CartEventRepository";
import { SavedCartWebsocketConnector } from "../common-cart/CartWebsocketConnector";
import { SequentialRequestProcessor } from "../../server/SequentialRequestProcessor";
import { CommonCart } from "../../model/CommonCartTypes";
import { CommonStatus, emitChangeItemQuantityCommandEvent } from "../../events/CartEvents";
export class SavedCartStoreImpl extends LazyStore implements ICartStore {
	_eventSourcedCartStore: EventSourcedCartStoreImpl;

	constructor(eventSourcedCartStore: EventSourcedCartStoreImpl) {
		super();
		this._eventSourcedCartStore = eventSourcedCartStore;
	}

	onArm = (): void => {
		this.listenTo(this._eventSourcedCartStore, this.trigger);
		this.listenTo(SavedCartActions.setItemQuantity, (bundleId, quantity) => {
			this.setItemQuantity(bundleId, quantity).catch((error) => {
				console.error("setItemQuantity in SavedCartStore failed with ", error);
			});
		});
		this.listenTo(SavedCartActions.deleteItem, (bundleId) => {
			this.removeItem(bundleId).catch((error) => {
				console.error("removeItem in SavedCartStore failed with ", error);
			});
		});
		this.listenTo(SavedCartActions.deleteItems, (bundleIds) => {
			this.removeItems(bundleIds).catch((error) => {
				console.error("removeItem in SavedCartStore failed with ", error);
			});
		});
		this.listenTo(SavedCartActions.deleteCart, () => {
			this.deleteCart().catch((error) => {
				console.error("deleteCart in SavedCartStore failed with ", error);
			});
		});
	};

	getCart(): CommonCart | null {
		const cart = this._eventSourcedCartStore.getOptimisticState();

		const cartContext = cart && cart.context;

		if (!cart || !cartContext) {
			return null;
		}

		const { country, storeId, cardholderNumber, fsdAddressId } = cartContext;
		const { cartId, cartVersion, customerId, totalPrices, items } = cart;

		return {
			cartId,
			cartVersion,
			country,
			storeId,
			fsdAddressId,
			customerId,
			// @ts-expect-error
			items,
			cardholderNumber,
			totalPrices,
		};
	}

	getOrderData = () => null;
	getDeliveryDate = () => null;
	isPending = () => this._eventSourcedCartStore.isPending();
	isEmployeeCart = () => false;

	async deleteCart() {
		const payload = new CartDeletedPayload();
		return this._eventSourcedCartStore.send(
			payload,
			(sessionContext, event) =>
				CustomerCartServerRequests.deleteCart({
					sessionContext,
					requestId: event.eventId,
					cartId: event.cartId,
				}),
			true,
		);
	}

	async addItem({ quantity, bundleId, comment }: AddItemParams) {
		if (quantity < minQuantity) {
			return null;
		}

		const scaledQuantity = floorToThreeDecimalPlaces(quantity);
		const payload = new ItemAddedPayload({
			bundleId,
			quantity: scaledQuantity,
			comment,
			addedTimestamp: new Date().getTime(),
			addedBy: SavedCartStoreImpl.getUserId(),
		});
		return this._eventSourcedCartStore.send(payload, (sessionContext, event) => {
			const response = CustomerCartServerRequests.postItem({
				sessionContext,
				requestId: event.eventId,
				cartId: event.cartId,
				bundleId,
				quantity: scaledQuantity,
				comment,
			});
			emitChangeItemQuantityCommandEvent({
				cartId: event.cartId,
				commonStatus: "SAVED" as CommonStatus,
				requestId: event.eventId,
				bundleId,
				previousQuantity: 0,
				newQuantity: scaledQuantity,
			});
			return response;
		});
	}

	async setItemQuantity(bundleId: string, quantity: number) {
		const cart = this._eventSourcedCartStore.getOptimisticState();

		if (cart && !cart.items[bundleId]) {
			return this.addItem({
				bundleId,
				quantity,
			});
		}

		if (quantity < minQuantity) {
			return this.removeItem(bundleId);
		}

		const scaledQuantity = floorToThreeDecimalPlaces(quantity);
		const payload = new ItemQuantitySetPayload({
			bundleId,
			quantity,
			addedTimestamp: new Date().getTime(),
			addedBy: SavedCartStoreImpl.getUserId(),
		});
		const previousQuantity = cart?.items[bundleId]?.quantity ?? 0;
		return this._eventSourcedCartStore.send(payload, (sessionContext, event) => {
			const response = CustomerCartServerRequests.putItemQuantity({
				sessionContext,
				requestId: event.eventId,
				cartId: event.cartId,
				bundleId,
				quantity: scaledQuantity,
			});
			emitChangeItemQuantityCommandEvent({
				cartId: event.cartId,
				commonStatus: "SAVED" as CommonStatus,
				requestId: event.eventId,
				bundleId,
				previousQuantity,
				newQuantity: scaledQuantity,
			});
			return response;
		});
	}

	async removeItem(bundleId: string) {
		const payload = new ItemRemovedPayload({
			bundleId,
		});
		const previousQuantity = this.getCart()?.items[bundleId]?.quantity ?? 0;
		return this._eventSourcedCartStore.send(payload, (sessionContext, event) => {
			const response = CustomerCartServerRequests.deleteItem({
				sessionContext,
				requestId: event.eventId,
				cartId: event.cartId,
				bundleId,
			});
			emitChangeItemQuantityCommandEvent({
				cartId: event.cartId,
				commonStatus: "SAVED" as CommonStatus,
				requestId: event.eventId,
				bundleId,
				previousQuantity,
				newQuantity: 0,
			});
			return response;
		});
	}

	async removeItems(bundleIds: string[]) {
		bundleIds.forEach(async (bundleId) => await this.removeItem(bundleId));
	}

	static getUserId(): string {
		return SessionStore.isEmployee() ? SessionStore.getEmployeeId() : SessionStore.getCustomerId();
	}
}
export const SavedCartStore = new SavedCartStoreImpl(
	new EventSourcedCartStoreImpl(
		"SavedCart",
		"SAVED",
		SavedCartContextStore,
		CustomerDataStore,
		CustomerCartEventRepository,
		SavedCartWebsocketConnector,
		new SequentialRequestProcessor(),
	),
);
